import { LineChart } from "@/components/charts/line-chart";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { LocalDate } from "@/components/ui/local-date";
import { toast } from "@/components/ui/use-toast";
import { INPUT_CLS } from "@/lib/analysis";
import { ApiError, api } from "@/lib/api";
import { useApiMutation, useRowMutation } from "@/lib/use-api-mutation";
import { formatCurrency } from "@/lib/utils";
import { Navigate, createFileRoute, useLoaderData } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

/**
 * `/billing/ai-spend` — admin/accountant. Spend of every LiteLLM virtual key
 * in CZK (CNB daily rate): totals, a 60-day chart, and per-key rows. A key
 * mapped to a customer can have a **closed month re-invoiced with a markup**
 * — one click creates a one-off CustomerCost line ("AI služby … 2026-06")
 * that the accountant picks into the regular monthly report. Key management
 * itself stays in the LiteLLM UI.
 *
 * E2E behaviour contract lives in `e2e/ai-spend.spec.ts`.
 */

type Rate = { rate: number; source: "cnb" | "fallback"; date: string };
type Charge = {
  id: string;
  month: string;
  spendUsd: number;
  rateCzk: number;
  rateSource: string;
  markupPct: number;
  amountCzk: number;
  customerCostId: string | null;
};
type SpendKey = {
  hash: string;
  alias: string | null;
  totalUsd: number;
  maxBudget: number | null;
  monthUsd: number;
  prevMonthUsd: number;
  mapping: { id: string; customerId: string; customerName: string; markupPct: number } | null;
  charges: Charge[];
};
type Overview = {
  enabled: boolean;
  rate: Rate | null;
  prevMonth?: string;
  days: Array<{ day: string; spendUsd: number }>;
  keys: SpendKey[];
};
type Customer = { id: string; name: string };

/** What the agents (Prokop and friends) cost on one project in a month. */
type ProjectSpend = {
  projectId: string;
  projectName: string;
  projectSlug: string;
  customerId: string | null;
  customerName: string | null;
  runs: number;
  spendUsd: number;
  charge: {
    id: string;
    amountCzk: number;
    markupPct: number;
    customerCostId: string | null;
    invoiced: boolean;
  } | null;
};

const DEFAULT_MARKUP = 50;

export const Route = createFileRoute("/_app/billing/ai-spend")({
  component: AiSpendPage,
});

function AiSpendPage() {
  const { user } = useLoaderData({ from: "/_app" });
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<ProjectSpend[]>([]);
  // Agent work is re-invoiced for the closed month, like the key spend above.
  const [agentMonth] = useState(() => {
    const d = new Date();
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  // Per-key mapping drafts (customer + markup) before saving.
  const [drafts, setDrafts] = useState<Record<string, { customerId: string; markupPct: number }>>(
    {},
  );

  const allowed = user.globalRole === "admin" || user.globalRole === "accountant";

  const load = useCallback(
    () =>
      api
        .get<Overview>("/api/ai-spend")
        .then((r) => {
          setData(r);
          setError(null);
        })
        .catch((err) =>
          setError(
            err instanceof ApiError && err.status === 502
              ? "The LiteLLM gateway didn't answer — check LITELLM_URL / LITELLM_ADMIN_KEY."
              : "Couldn't load AI spend.",
          ),
        ),
    [],
  );

  const loadProjects = useCallback(
    () =>
      api
        .get<{ projects: ProjectSpend[] }>(`/api/ai-spend/agent-projects?month=${agentMonth}`)
        .then((r) => setProjects(r.projects))
        .catch(() => {}),
    [agentMonth],
  );

  useEffect(() => {
    if (!allowed) return;
    void load();
    void loadProjects();
    api
      .get<{ customers: Customer[] }>("/api/customers?pageSize=200")
      .then((r) => setCustomers(r.customers.map((c) => ({ id: c.id, name: c.name }))))
      .catch(() => {});
  }, [allowed, load, loadProjects]);

  const saveMappingMut = useRowMutation(
    (_key: SpendKey, body: Record<string, unknown>) => api.put("/api/ai-spend/mappings", body),
    {
      rowKey: (key) => key.hash,
      onSuccess: (_res, key) => {
        toast({ title: "Mapping saved" });
        load();
        setDrafts((prev) => {
          const { [key.hash]: _, ...rest } = prev;
          return rest;
        });
      },
      onError: () => toast({ title: "Couldn't save the mapping.", variant: "destructive" }),
    },
  );
  const chargeMut = useRowMutation(
    (key: SpendKey, month: string) =>
      api.post<{ amountCzk: number; customerName: string }>("/api/ai-spend/charges", {
        keyHash: key.hash,
        month,
      }),
    {
      rowKey: (key) => key.hash,
      onSuccess: (res) => {
        toast({
          title: `Charged ${formatCurrency(res.amountCzk)} to ${res.customerName}`,
          description: "The cost line is ready to be picked into the monthly report.",
        });
        load();
      },
      errorMessage: (err) =>
        (err instanceof ApiError && err.detail && typeof err.detail === "object"
          ? (err.detail as { error?: string }).error
          : undefined) ?? "Couldn't create the charge.",
      onError: (msg) => toast({ title: msg, variant: "destructive" }),
    },
  );
  const undoMut = useApiMutation(
    (chargeId: string) => api.delete(`/api/ai-spend/charges/${encodeURIComponent(chargeId)}`),
    {
      onSuccess: () => {
        toast({ title: "Charge removed" });
        load();
      },
      errorMessage: (err) =>
        (err instanceof ApiError && err.detail && typeof err.detail === "object"
          ? (err.detail as { error?: string }).error
          : undefined) ?? "Couldn't remove the charge.",
      onError: (msg) => toast({ title: msg, variant: "destructive" }),
    },
  );

  if (!allowed) return <Navigate to="/" />;

  const rate = data?.rate ?? null;
  const czk = (usd: number) => (rate ? formatCurrency(usd * rate.rate) : "—");
  const usd = (v: number) => formatCurrency(v, "USD");

  // One "busy" key across the save + charge actions on a row — either
  // in flight disables both buttons, matching the old shared flag.
  const busyKey = saveMappingMut.runningKey ?? chargeMut.runningKey;

  function saveMapping(key: SpendKey) {
    const draft = drafts[key.hash] ?? {
      customerId: key.mapping?.customerId ?? "",
      markupPct: key.mapping?.markupPct ?? DEFAULT_MARKUP,
    };
    if (!draft.customerId) return;
    saveMappingMut.run(key, {
      keyHash: key.hash,
      keyAlias: key.alias,
      customerId: draft.customerId,
      markupPct: draft.markupPct,
    });
  }

  function chargeMonth(key: SpendKey, month: string) {
    chargeMut.run(key, month);
  }

  function undoCharge(chargeId: string) {
    undoMut.mutate(chargeId);
  }

  const monthTotal = data?.keys.reduce((acc, k) => acc + k.monthUsd, 0) ?? 0;
  const prevTotal = data?.keys.reduce((acc, k) => acc + k.prevMonthUsd, 0) ?? 0;
  const series =
    data?.days.map((d) => ({ day: d.day, czk: rate ? d.spendUsd * rate.rate : 0 })) ?? [];

  const prevMonth = data?.prevMonth;
  /** The unsaved (or saved) mapping draft a row's editors bind to. */
  const draftFor = (key: SpendKey) =>
    drafts[key.hash] ?? {
      customerId: key.mapping?.customerId ?? "",
      markupPct: key.mapping?.markupPct ?? DEFAULT_MARKUP,
    };

  // Columns close over the page state (drafts, customers, busyKey) — the
  // editable cells are plain `cell` renderers, the state stays up here.
  const columns: Array<DataTableColumn<SpendKey>> = [
    {
      key: "key",
      header: "Key",
      cell: (key) => (
        <>
          <span className="font-medium">{key.alias ?? key.hash.slice(0, 12)}</span>
          {key.maxBudget !== null && (
            <span className="ml-1 text-xs text-muted-foreground">
              / budget {usd(key.maxBudget)}
            </span>
          )}
        </>
      ),
    },
    {
      key: "month",
      header: "This month",
      headClassName: "text-right",
      cellClassName: "whitespace-nowrap text-right tabular-nums",
      cell: (key) => (
        <>
          <div>{czk(key.monthUsd)}</div>
          <div className="text-xs text-muted-foreground">{usd(key.monthUsd)}</div>
        </>
      ),
    },
    {
      key: "prevMonth",
      header: "Last month",
      headClassName: "text-right",
      cellClassName: "whitespace-nowrap text-right tabular-nums",
      cell: (key) => (
        <>
          <div>{czk(key.prevMonthUsd)}</div>
          <div className="text-xs text-muted-foreground">{usd(key.prevMonthUsd)}</div>
        </>
      ),
    },
    {
      key: "lifetime",
      header: "Lifetime",
      headClassName: "text-right",
      cellClassName: "whitespace-nowrap text-right tabular-nums text-muted-foreground",
      cell: (key) => usd(key.totalUsd),
    },
    {
      key: "customer",
      header: "Customer",
      cellClassName: "min-w-44",
      cell: (key) => (
        <Combobox
          options={customers.map((cust) => ({ value: cust.id, label: cust.name }))}
          value={draftFor(key).customerId}
          onChange={(v) =>
            setDrafts((prev) => ({
              ...prev,
              [key.hash]: { ...draftFor(key), customerId: v },
            }))
          }
          placeholder="— internal —"
          aria-label={`Customer for ${key.alias ?? key.hash}`}
        />
      ),
    },
    {
      key: "markup",
      header: "Markup %",
      headClassName: "text-right",
      cellClassName: "w-24 text-right",
      cell: (key) => (
        <input
          type="number"
          min={0}
          max={500}
          value={draftFor(key).markupPct}
          onChange={(e) =>
            setDrafts((prev) => ({
              ...prev,
              [key.hash]: { ...draftFor(key), markupPct: Number(e.target.value) },
            }))
          }
          className={`${INPUT_CLS} h-8 w-20 text-right`}
          aria-label={`Markup for ${key.alias ?? key.hash}`}
        />
      ),
    },
    {
      key: "reinvoice",
      header: "Re-invoice",
      cell: (key) => {
        const draft = draftFor(key);
        const prevCharge = key.charges.find((ch) => ch.month === prevMonth);
        const dirty =
          draft.customerId !== (key.mapping?.customerId ?? "") ||
          draft.markupPct !== (key.mapping?.markupPct ?? DEFAULT_MARKUP);
        return (
          <div className="flex flex-wrap items-center gap-2">
            {dirty && draft.customerId && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={busyKey === key.hash}
                onClick={() => saveMapping(key)}
                data-testid="save-mapping"
              >
                Save mapping
              </Button>
            )}
            {!dirty && key.mapping && prevMonth && !prevCharge && (
              <Button
                type="button"
                size="sm"
                disabled={busyKey === key.hash || key.prevMonthUsd <= 0}
                onClick={() => chargeMonth(key, prevMonth)}
                data-testid="charge-month"
              >
                Charge {prevMonth}
              </Button>
            )}
            {prevCharge && (
              <span className="text-xs text-muted-foreground" data-testid="charged-badge">
                ✓ {prevCharge.month}: {formatCurrency(prevCharge.amountCzk)}
                {prevCharge.customerCostId && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="ml-1 h-6 px-1 text-xs"
                    onClick={() => undoCharge(prevCharge.id)}
                    data-testid="undo-charge"
                  >
                    undo
                  </Button>
                )}
              </span>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">AI Spend</h1>
          <p className="text-muted-foreground mt-1">
            What every gateway key burns, in CZK — and one-click re-invoicing of a closed month to
            the mapped customer (with markup).
          </p>
        </div>
        {rate && (
          <Badge
            variant={rate.source === "cnb" ? "secondary" : "destructive"}
            data-testid="rate-badge"
          >
            {rate.source === "cnb" ? "ČNB" : "fallback"} {rate.rate.toFixed(3).replace(".", ",")}{" "}
            Kč/USD · {rate.date}
          </Badge>
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <AgentProjectsCard month={agentMonth} projects={projects} onChanged={loadProjects} />

      <ModelPricesCard />

      {data && !data.enabled && (
        <p className="text-sm text-muted-foreground" data-testid="ai-spend-off">
          The LiteLLM gateway isn't configured. Set <code>LITELLM_URL</code> and{" "}
          <code>LITELLM_ADMIN_KEY</code> (a key created with <code>get_spend_routes</code>{" "}
          permission is enough — don't use the master key).
        </p>
      )}

      {data?.enabled && (
        <>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">This month</CardTitle>
              </CardHeader>
              <CardContent data-testid="month-total">
                <div className="text-2xl font-bold">{czk(monthTotal)}</div>
                <div className="text-xs text-muted-foreground">{usd(monthTotal)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">
                  Last month{data.prevMonth ? ` (${data.prevMonth})` : ""}
                </CardTitle>
              </CardHeader>
              <CardContent data-testid="prev-month-total">
                <div className="text-2xl font-bold">{czk(prevTotal)}</div>
                <div className="text-xs text-muted-foreground">{usd(prevTotal)}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Daily spend (60 days, CZK)</CardTitle>
              </CardHeader>
              <CardContent>
                <LineChart
                  data={series.map((d) => ({ label: d.day.slice(5), value: d.czk }))}
                  showDots={false}
                  height={80}
                  formatValue={(v) => formatCurrency(v)}
                />
              </CardContent>
            </Card>
          </div>

          <DataTable
            columns={columns}
            rows={data.keys}
            rowKey={(key) => key.hash}
            rowTestId={() => "spend-key-row"}
            density="compact"
            emptyState="No gateway keys yet."
          />

          <p className="text-xs text-muted-foreground">
            Charging a month creates a one-off customer cost („AI služby … {data.prevMonth}") — pick
            it into the monthly report on the Billing page as usual. Undo works until the cost lands
            on an invoice. Key management (creating keys, hard budgets) stays in the{" "}
            <a
              href="https://ai.netlion.cz/ui/"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              LiteLLM UI
            </a>
            .
          </p>
        </>
      )}
    </div>
  );
}

/**
 * Agent work per project — what Prokop (and any other acting agent) burned on
 * each project in the closed month, and the one-click re-invoice. Same shape
 * as the per-key charge above: snapshot × CNB rate × markup → one-off cost.
 */
function AgentProjectsCard({
  month,
  projects,
  onChanged,
}: {
  month: string;
  projects: ProjectSpend[];
  onChanged: () => void;
}) {
  const chargeMut = useRowMutation(
    (row: ProjectSpend) =>
      api.post("/api/ai-spend/agent-charges", { projectId: row.projectId, month }),
    {
      rowKey: (row) => row.projectId,
      onSuccess: () => {
        toast({ title: "Charged", variant: "success" });
        onChanged();
      },
      onError: (msg) =>
        toast({ title: "Couldn't charge", description: msg, variant: "destructive" }),
    },
  );
  const undoMut = useApiMutation((id: string) => api.delete(`/api/ai-spend/agent-charges/${id}`), {
    onSuccess: () => {
      toast({ title: "Charge withdrawn", variant: "success" });
      onChanged();
    },
    fallbackMessage: "Try again.",
    onError: (msg) => toast({ title: "Couldn't undo", description: msg, variant: "destructive" }),
  });

  const columns: Array<DataTableColumn<ProjectSpend>> = [
    { key: "project", header: "Project", cell: (row) => row.projectName },
    {
      key: "customer",
      header: "Customer",
      cell: (row) => row.customerName ?? <span className="text-muted-foreground">—</span>,
    },
    {
      key: "runs",
      header: "Runs",
      headClassName: "text-right",
      cellClassName: "text-right",
      cell: (row) => row.runs,
    },
    {
      key: "cost",
      header: "Our cost",
      headClassName: "text-right",
      cellClassName: "text-right",
      cell: (row) => `$${row.spendUsd.toFixed(2)}`,
    },
    {
      key: "charge",
      header: `Re-invoice ${month}`,
      headClassName: "text-right",
      cellClassName: "text-right",
      cell: (row) => {
        if (row.charge) {
          return (
            <div className="flex items-center justify-end gap-2">
              <Badge variant="secondary">{formatCurrency(row.charge.amountCzk)}</Badge>
              {row.charge.invoiced ? (
                <span className="text-xs text-muted-foreground">invoiced</span>
              ) : (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={undoMut.pending}
                  onClick={() => undoMut.mutate(row.charge?.id ?? "")}
                >
                  Undo
                </Button>
              )}
            </div>
          );
        }
        return (
          <Button
            size="sm"
            variant="outline"
            disabled={!row.customerId || chargeMut.runningKey !== null}
            onClick={() => chargeMut.run(row)}
          >
            {row.customerId ? "Charge" : "No customer"}
          </Button>
        );
      },
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Agent work per project — {month}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          What the agents spent verifying each project's tickets. Charging adds a one-off customer
          cost („AI asistence — projekt — {month}") with the standard markup, which you then pick
          into that customer's monthly report. Projects without a customer can't be charged.
        </p>
        <DataTable
          columns={columns}
          rows={projects}
          rowKey={(row) => row.projectId}
          rowTestId={() => "agent-project-row"}
          density="compact"
          emptyState="No agent work on projects in that month."
        />
      </CardContent>
    </Card>
  );
}

type ModelPrice = {
  model: string;
  inputPerMTok: number;
  outputPerMTok: number;
  cacheWritePerMTok: number;
  cacheReadPerMTok: number;
  source: string;
  updatedAt: string;
};
type ModelPrices = {
  gatewayConfigured: boolean;
  cache: { models: number; loadedAt: string | null };
  models: ModelPrice[];
};

/**
 * What each model costs per million tokens — the prices every agent-run cost
 * on this page is computed from. They come from the gateway's own catalogue
 * and refresh daily, so the ledger follows a provider reprice on its own;
 * "Refresh now" is for when you don't want to wait for the nightly job.
 */
function ModelPricesCard() {
  const [data, setData] = useState<ModelPrices | null>(null);

  const load = useCallback(
    () =>
      api
        .get<ModelPrices>("/api/ai-spend/model-prices")
        .then(setData)
        .catch(() => {}),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const refresh = useApiMutation(
    () => api.post<{ updated: number }>("/api/ai-spend/model-prices/refresh", {}),
    {
      onSuccess: (res) => {
        toast({ title: `Refreshed ${res.updated} model price(s) from the gateway.` });
        void load();
      },
      fallbackMessage: "Couldn't refresh the model prices.",
      onError: (msg) => toast({ title: msg, variant: "destructive" }),
    },
  );

  const refreshedAt = data?.models.reduce<string | null>(
    (latest, m) => (latest === null || m.updatedAt > latest ? m.updatedAt : latest),
    null,
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle>Model prices</CardTitle>
        <Button
          size="sm"
          variant="outline"
          disabled={refresh.pending}
          onClick={() => void refresh.mutate()}
          data-testid="refresh-model-prices"
        >
          {refresh.pending ? "Refreshing…" : "Refresh now"}
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          USD per million tokens, read from the LiteLLM gateway's own catalogue and refreshed daily
          — this is what every agent run on this page is costed with. A model the gateway doesn't
          price falls back to Hub's built-in rates.
        </p>
        {data && data.models.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="model-prices-empty">
            {data.gatewayConfigured
              ? "No prices loaded yet — the daily job hasn't run. Use Refresh now."
              : "The LiteLLM gateway isn't configured, so Hub is using its built-in fallback rates."}
          </p>
        ) : (
          <>
            <DataTable
              columns={[
                { key: "model", header: "Model", cell: (r: ModelPrice) => r.model },
                {
                  key: "in",
                  header: "Input",
                  headClassName: "text-right",
                  cellClassName: "text-right",
                  cell: (r: ModelPrice) => `$${r.inputPerMTok}`,
                },
                {
                  key: "out",
                  header: "Output",
                  headClassName: "text-right",
                  cellClassName: "text-right",
                  cell: (r: ModelPrice) => `$${r.outputPerMTok}`,
                },
                {
                  key: "cw",
                  header: "Cache write",
                  headClassName: "text-right",
                  cellClassName: "text-right",
                  cell: (r: ModelPrice) => `$${r.cacheWritePerMTok}`,
                },
                {
                  key: "cr",
                  header: "Cache read",
                  headClassName: "text-right",
                  cellClassName: "text-right",
                  cell: (r: ModelPrice) => `$${r.cacheReadPerMTok}`,
                },
              ]}
              rows={data?.models ?? []}
              rowKey={(r) => r.model}
              rowTestId={() => "model-price-row"}
              density="compact"
              emptyState="No model prices stored."
            />
            {refreshedAt && (
              <p className="text-xs text-muted-foreground" data-testid="model-prices-updated">
                Last refreshed <LocalDate value={refreshedAt} withTime />
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
